import { useEffect, useState } from 'react';
import { studentsAPI } from '../../services/api';
import { mapUserStudentToEditForm } from '../../utils/mapUserStudentToEditForm';
import SearchableSelect from '../Form/SearchableSelect';

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

const PROFILE_PREFERENCE_OPTIONS = [
  { value: 'Physical_Design', label: 'Physical Design' },
  { value: 'Analog_Design', label: 'Analog Design' },
  { value: 'DV_Profile', label: 'DV Profile' },
  { value: 'Other', label: 'Other' },
];

const MARKS_TYPE_OPTIONS = [
  { value: 'Percentage', label: 'Percentage' },
  { value: 'CGPA', label: 'CGPA' },
];

const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const inputClass =
  'w-full px-3 py-2 rounded-lg border text-sm theme-focus';
const labelClass = 'block text-xs font-medium theme-text-secondary mb-1';

function buildInitialForm(student) {
  if (!student) return null;
  return mapUserStudentToEditForm(
    {
      email: student.emailAddress || '',
      firstName: '',
      lastName: '',
      role: 'student',
    },
    student
  );
}

function buildSelfUpdatePayload(form) {
  return {
    firstName: form.firstName,
    middleName: form.middleName,
    lastName: form.lastName,
    phoneNumber: form.mobile,
    gender: form.gender || null,
    dob: form.dob || null,
    linkedIn: form.linkedIn || null,
    pincode: form.pincode || null,
    currentAddress: form.address || null,
    currentLocation: form.currentCity || null,
    permanentAddress: form.permanentAddress || null,
    joiningKitAddress: form.joiningKitAddress || null,
    joiningKitReceived: form.joiningKitReceived || null,
    parentsName: form.parentsName || null,
    parentsPhone: form.parentsPhone || null,
    parentsEmail: form.parentsEmail || null,
    diplomaDone: form.diplomaDone || null,
    profilePreference: form.profilePreference || null,
    motivation: form.motivation || null,
    resumeUrl: form.resumeUrl || null,
    tenthMarks: form.tenthMarks,
    tenthYear: form.tenthYear,
    tenthMarksType: form.tenthMarksType,
    twelfthMarks: form.twelfthMarks,
    twelfthYear: form.twelfthYear,
    twelfthMarksType: form.twelfthMarksType,
    btechMarks: form.btechMarks || form.percentage,
    btechYear: form.btechYear || form.yearOfPassing,
    btechMarksType: form.btechMarksType || form.percentageType,
    btechInstitute: form.btechInstitute || form.university,
    stream: form.stream || form.department || form.btechSpecialization,
    department: form.department || form.stream || form.btechSpecialization,
    btechSpecialization: form.btechSpecialization || form.stream || form.department,
    mtechMarks: form.mtechMarks,
    mtechYear: form.mtechYear,
    mtechMarksType: form.mtechMarksType,
    mtechInstitute: form.mtechInstitute,
    mtechSpecialization: form.mtechSpecialization,
    phdDone: form.phdDone,
    phdYear: form.phdYear,
    degree: form.degree || 'B.Tech',
    university: form.university || form.btechInstitute,
    yearOfPassing: form.yearOfPassing || form.btechYear,
    percentage: form.percentage || form.btechMarks,
    percentageType: form.percentageType || form.btechMarksType,
    hasGapInEducation: form.hasGapInEducation,
    gapInEducationJustification: form.gapInEducationJustification,
    hasGapAfterHighestEducation: form.hasGapAfterHighestEducation,
    gapAfterHighestEducationJustification: form.gapAfterHighestEducationJustification,
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Student self-service editor for personal details + education (SMS, not Zoho).
 */
const StudentProfileEditCard = ({ student, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => buildInitialForm(student));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!editing) {
      setForm(buildInitialForm(student));
    }
  }, [student, editing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setForm(buildInitialForm(student));
    setEditing(false);
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await studentsAPI.updateMe(buildSelfUpdatePayload(form));
      setSuccess('Your details were saved.');
      setEditing(false);
      if (typeof onSaved === 'function') await onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!student || !form) return null;

  const inputStyle = {
    backgroundColor: 'var(--color-background)',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text)',
  };

  return (
    <section
      id="sd-my-details"
      className="rounded-xl border p-5 shadow-sm theme-animate"
      style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold theme-text-gradient">My details</h2>
          <p className="text-sm theme-text-secondary mt-1">
            Update your personal and education information. Batch, placement, and status are managed by your institute.
          </p>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white theme-focus"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Edit details
          </button>
        ) : null}
      </div>

      {error ? (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="mb-3 text-sm" style={{ color: 'var(--color-success, #15803d)' }}>
          {success}
        </p>
      ) : null}

      {!editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <ReadOnly label="Phone" value={form.mobile} />
          <ReadOnly label="Gender" value={form.gender} />
          <ReadOnly label="Date of birth" value={form.dob} />
          <ReadOnly label="Current city" value={form.currentCity} />
          <ReadOnly label="LinkedIn" value={form.linkedIn} />
          <ReadOnly label="Profile preference" value={form.profilePreference} />
          <ReadOnly label="10th marks" value={form.tenthMarks} />
          <ReadOnly label="12th marks" value={form.twelfthMarks} />
          <ReadOnly label="B.Tech / highest" value={form.percentage || form.btechMarks} />
          <ReadOnly label="Year of passing" value={form.yearOfPassing || form.btechYear} />
          <ReadOnly label="Institute" value={form.btechInstitute || form.university} />
          <ReadOnly label="B.Tech specialization" value={form.btechSpecialization || form.stream || form.department} />
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
              Personal
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name">
                <input name="firstName" className={inputClass} style={inputStyle} value={form.firstName || ''} onChange={handleChange} required />
              </Field>
              <Field label="Middle name">
                <input name="middleName" className={inputClass} style={inputStyle} value={form.middleName || ''} onChange={handleChange} />
              </Field>
              <Field label="Last name">
                <input name="lastName" className={inputClass} style={inputStyle} value={form.lastName || ''} onChange={handleChange} required />
              </Field>
              <Field label="Phone">
                <input name="mobile" className={inputClass} style={inputStyle} value={form.mobile || ''} onChange={handleChange} />
              </Field>
              <Field label="Gender">
                <SearchableSelect
                  name="gender"
                  className="w-full"
                  value={form.gender || ''}
                  onChange={handleChange}
                  options={GENDER_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <Field label="Date of birth">
                <input type="date" name="dob" className={inputClass} style={inputStyle} value={form.dob || ''} onChange={handleChange} />
              </Field>
              <Field label="LinkedIn">
                <input name="linkedIn" className={inputClass} style={inputStyle} value={form.linkedIn || ''} onChange={handleChange} />
              </Field>
              <Field label="Current city">
                <input name="currentCity" className={inputClass} style={inputStyle} value={form.currentCity || ''} onChange={handleChange} />
              </Field>
              <Field label="Pincode">
                <input name="pincode" className={inputClass} style={inputStyle} value={form.pincode || ''} onChange={handleChange} />
              </Field>
              <Field label="Profile preference">
                <SearchableSelect
                  name="profilePreference"
                  className="w-full"
                  value={form.profilePreference || ''}
                  onChange={handleChange}
                  options={PROFILE_PREFERENCE_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Current address">
                  <textarea name="address" rows={2} className={inputClass} style={inputStyle} value={form.address || ''} onChange={handleChange} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Permanent address">
                  <textarea name="permanentAddress" rows={2} className={inputClass} style={inputStyle} value={form.permanentAddress || ''} onChange={handleChange} />
                </Field>
              </div>
              <Field label="Parents name">
                <input name="parentsName" className={inputClass} style={inputStyle} value={form.parentsName || ''} onChange={handleChange} />
              </Field>
              <Field label="Parents phone">
                <input name="parentsPhone" className={inputClass} style={inputStyle} value={form.parentsPhone || ''} onChange={handleChange} />
              </Field>
              <Field label="Parents email">
                <input type="email" name="parentsEmail" className={inputClass} style={inputStyle} value={form.parentsEmail || ''} onChange={handleChange} />
              </Field>
              <Field label="Resume URL">
                <input name="resumeUrl" className={inputClass} style={inputStyle} value={form.resumeUrl || ''} onChange={handleChange} />
              </Field>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
              Education
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="10th marks">
                <input name="tenthMarks" className={inputClass} style={inputStyle} value={form.tenthMarks || ''} onChange={handleChange} />
              </Field>
              <Field label="10th year">
                <input name="tenthYear" className={inputClass} style={inputStyle} value={form.tenthYear || ''} onChange={handleChange} />
              </Field>
              <Field label="10th marks type">
                <SearchableSelect
                  name="tenthMarksType"
                  className="w-full"
                  value={form.tenthMarksType || 'Percentage'}
                  onChange={handleChange}
                  options={MARKS_TYPE_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <Field label="Diploma done">
                <SearchableSelect
                  name="diplomaDone"
                  className="w-full"
                  value={form.diplomaDone || ''}
                  onChange={handleChange}
                  options={YES_NO_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <Field label="12th marks">
                <input name="twelfthMarks" className={inputClass} style={inputStyle} value={form.twelfthMarks || ''} onChange={handleChange} />
              </Field>
              <Field label="12th year">
                <input name="twelfthYear" className={inputClass} style={inputStyle} value={form.twelfthYear || ''} onChange={handleChange} />
              </Field>
              <Field label="12th marks type">
                <SearchableSelect
                  name="twelfthMarksType"
                  className="w-full"
                  value={form.twelfthMarksType || 'Percentage'}
                  onChange={handleChange}
                  options={MARKS_TYPE_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <Field label="B.Tech / highest marks">
                <input name="percentage" className={inputClass} style={inputStyle} value={form.percentage || ''} onChange={handleChange} />
              </Field>
              <Field label="Year of passing">
                <input name="yearOfPassing" className={inputClass} style={inputStyle} value={form.yearOfPassing || ''} onChange={handleChange} />
              </Field>
              <Field label="Marks type">
                <SearchableSelect
                  name="percentageType"
                  className="w-full"
                  value={form.percentageType || 'Percentage'}
                  onChange={handleChange}
                  options={MARKS_TYPE_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <Field label="Institute / university">
                <input name="btechInstitute" className={inputClass} style={inputStyle} value={form.btechInstitute || ''} onChange={handleChange} />
              </Field>
              <Field label="B.Tech specialization / branch">
                <input name="btechSpecialization" className={inputClass} style={inputStyle} value={form.btechSpecialization || form.stream || ''} onChange={handleChange} />
              </Field>
              <Field label="M.Tech marks">
                <input name="mtechMarks" className={inputClass} style={inputStyle} value={form.mtechMarks || ''} onChange={handleChange} />
              </Field>
              <Field label="M.Tech year">
                <input name="mtechYear" className={inputClass} style={inputStyle} value={form.mtechYear || ''} onChange={handleChange} />
              </Field>
              <Field label="M.Tech institute">
                <input name="mtechInstitute" className={inputClass} style={inputStyle} value={form.mtechInstitute || ''} onChange={handleChange} />
              </Field>
              <Field label="Gap during education">
                <SearchableSelect
                  name="hasGapInEducation"
                  className="w-full"
                  value={form.hasGapInEducation || ''}
                  onChange={handleChange}
                  options={YES_NO_OPTIONS}
                  placeholder="Select"
                />
              </Field>
              <Field label="Gap justification">
                <input
                  name="gapInEducationJustification"
                  className={inputClass}
                  style={inputStyle}
                  value={form.gapInEducationJustification || ''}
                  onChange={handleChange}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Motivation / goals">
                  <textarea name="motivation" rows={2} className={inputClass} style={inputStyle} value={form.motivation || ''} onChange={handleChange} />
                </Field>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white theme-focus disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium border theme-focus disabled:opacity-50"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

function ReadOnly({ label, value }) {
  return (
    <div>
      <p className="theme-text-secondary text-xs uppercase tracking-wide">{label}</p>
      <p className="font-medium" style={{ color: 'var(--color-text)' }}>
        {value || '—'}
      </p>
    </div>
  );
}

export default StudentProfileEditCard;
